import { useContext, useEffect, useMemo, useState } from 'react';
import { ClientEvent, KnownMembership, MatrixEventEvent, Room, RoomEvent } from 'matrix-js-sdk';
import MatrixContext from 'services/matrix-connection/context/MatrixContext';
import { markRoomAsDirect } from 'services/matrix-connection/directRooms';
import { toMatrixUserId } from 'services/matrix-connection/matrixIdentifiers';
import { useMatrixMemberDirectory, useMatrixRoomSummaries } from 'shared-state/matrix-chat/hooks';
import { fetchMatrixMemberDirectory } from 'shared-state/matrix-chat/loadMemberDirectory';
import { getDirectRoomIdSet, mapRoomsToSummaries } from './roomSummaries';

const MatrixAdapter: React.FC = () => {
    const { client } = useContext(MatrixContext);
    const [, setSummaries] = useMatrixRoomSummaries();
    const [memberDirectory, setMemberDirectory] = useMatrixMemberDirectory();
    const [isDirectoryLoaded, setIsDirectoryLoaded] = useState(false);

    useEffect(() => {
        if (!client) return;
        fetchMatrixMemberDirectory()
            .then(setMemberDirectory)
            .catch(() => setMemberDirectory([]))
            .finally(() => setIsDirectoryLoaded(true));
    }, [client, setMemberDirectory]);

    // Keyed by matrix id (derived from each org member's email) rather than campus uid, since
    // room members/senders are only ever identified by matrix id.
    const memberProfilesByMatrixId = useMemo(() => {
        const homeserverDomain = client?.getDomain() ?? '';
        return Object.fromEntries(
            memberDirectory.map((member) => [toMatrixUserId(member.email, homeserverDomain), { name: member.name, profilePic: member.profilePic }])
        );
    }, [client, memberDirectory]);

    useEffect(() => {
        if (!client) return;

        const refresh = () => {
            const directRoomIds = getDirectRoomIdSet(client);
            setSummaries(mapRoomsToSummaries(client.getRooms(), directRoomIds, client.getUserId() ?? '', memberProfilesByMatrixId, isDirectoryLoaded));
        };

        // Rooms created by (or inviting) this user arrive as invites, not joins - without
        // auto-joining, a chat created for another user never actually becomes usable for them.
        const autoJoinOnInvite = (room: Room, membership: string) => {
            if (membership !== KnownMembership.Invite) return;
            void client.joinRoom(room.roomId).then(() => {
                // The creator's is_direct flag lands on our own invite membership event, not our
                // account data - mirror the same m.direct bookkeeping createRoom.ts does on the
                // creating side, so this account's own room-list/avatar/dedup logic also treats
                // the room as direct rather than a group.
                const memberEvent = room.getMember(client.getUserId() ?? '')?.events.member;
                const inviterId = memberEvent?.getSender();
                if (memberEvent?.getContent()?.is_direct && inviterId) {
                    void markRoomAsDirect(client, inviterId, room.roomId);
                }
            });
        };

        refresh();
        client.on(ClientEvent.Sync, refresh);
        client.on(ClientEvent.Room, refresh);
        client.on(RoomEvent.Timeline, refresh);
        client.on(RoomEvent.Receipt, refresh);
        client.on(RoomEvent.MyMembership, autoJoinOnInvite);
        // Encrypted messages decrypt asynchronously after Room.timeline already fired, so the
        // last-message preview (filtered to type "m.room.message") would otherwise stay stale
        // until an unrelated event happened to trigger another refresh.
        client.on(MatrixEventEvent.Decrypted, refresh);

        return () => {
            client.off(ClientEvent.Sync, refresh);
            client.off(ClientEvent.Room, refresh);
            client.off(RoomEvent.Timeline, refresh);
            client.off(RoomEvent.Receipt, refresh);
            client.off(RoomEvent.MyMembership, autoJoinOnInvite);
            client.off(MatrixEventEvent.Decrypted, refresh);
        };
    }, [client, setSummaries, memberProfilesByMatrixId, isDirectoryLoaded]);

    return null;
};

export default MatrixAdapter;
